import React, { useEffect } from "react";
import classNames from "classnames";
import { makeStyles } from "@material-ui/core/styles";
import Header from "../LandingPage/Sections/Header";

const useStyles = makeStyles({
  container: {
    zIndex: "12",
    color: "#333",
    paddingRight: "15px",
    paddingLeft: "15px",
    marginRight: "auto",
    marginLeft: "auto",
    width: "100%",
    "@media (min-width: 576px)": {
      maxWidth: "540px"
    },
    "@media (min-width: 768px)": {
      maxWidth: "720px"
    },
    "@media (min-width: 992px)": {
      maxWidth: "960px"
    },
    "@media (min-width: 1200px)": {
      maxWidth: "1140px"
    }
  },
  main: {
    background: "#FFFFFF",
    position: "relative",
    zIndex: "3",
    padding: "40px"
  },
  mainRaised: {
    margin: "100px auto 0",
    borderRadius: "6px",
    boxShadow:
      "0 16px 24px 2px rgba(0, 0, 0, 0.14), 0 6px 30px 5px rgba(0, 0, 0, 0.12), 0 8px 10px -5px rgba(0, 0, 0, 0.2)"
  },
  title: {
    fontSize: "2.5rem",
    fontWeight: "700",
    marginBottom: "16px",
    marginTop: "0px",
    color: "#333"
  },
  subtitle: {
    fontSize: "0.875rem",
    color: "#999",
    marginBottom: "32px"
  },
  section: {
    marginBottom: "32px"
  },
  sectionTitle: {
    fontSize: "1.25rem",
    fontWeight: "600",
    marginTop: "24px",
    marginBottom: "16px",
    color: "#333"
  },
  subsectionTitle: {
    fontSize: "1rem",
    fontWeight: "500",
    marginTop: "16px",
    marginBottom: "12px",
    color: "#333"
  },
  paragraph: {
    fontSize: "1rem",
    lineHeight: "1.6",
    marginBottom: "16px",
    color: "#333"
  },
  list: {
    marginLeft: "24px",
    marginBottom: "16px",
    "& li": {
      marginBottom: "8px",
      color: "#333"
    }
  },
  link: {
    color: "#1976d2",
    textDecoration: "none",
    "&:hover": {
      textDecoration: "underline"
    }
  }
});

const PrivacyPolicy = () => {
  const classes = useStyles();

  useEffect(() => {
    document.body.classList.add('policy');

    return () => document.body.classList.remove('policy');
  }, []);

  return (
    <div style={{paddingBottom: 100}}>
      <Header />
      <div className={classNames(classes.main, classes.mainRaised, classes. container)}>
        <div>
          <h1 className={classes.title}>Privacy Policy</h1>
          <p className={classes.subtitle}>
            Last updated: <strong>January 2026</strong>
          </p>

          <div className={classes.section}>
            <p className={classes.paragraph}>
              Databycloud, Inc. ("Databycloud", "we", "us", or "our") operates the Databycloud platform
              ("Service"), a secure access-sharing and identity management solution for enterprise
              applications including Workday, NetSuite, SAP, Oracle, Salesforce, and related systems.
              This Privacy Policy describes how we collect, use, disclose, and safeguard your information
              when you use our Service. Please read this policy carefully. By accessing or using our
              Service, you acknowledge that you have read, understood, and agree to be bound by this
              Privacy Policy.
            </p>

            <h2 className={classes.sectionTitle}>1. Information We Collect</h2>
            <p className={classes.paragraph}>
              We collect information to provide, maintain, and improve our Service. The types of
              information we collect depend on how you interact with our platform.
            </p>

            <h3 className={classes.subsectionTitle}>1.1 Information You Provide Directly</h3>
            <ul className={classes.list}>
              <li><strong>Account Information:</strong> Name, email address, phone number, job title, and company name when you register for an account</li>
              <li><strong>Profile Data:</strong> Username, password, profile picture, and preferences you set within the Service</li>
              <li><strong>Billing Information:</strong> Payment card details, billing address, and transaction history (processed securely through third-party payment processors)</li>
              <li><strong>Communications:</strong> Information you provide when contacting our support team, participating in surveys, or providing feedback</li>
              <li><strong>Enterprise Configuration:</strong> Connection settings, access policies, and integration configurations for your enterprise applications</li>
            </ul>

            <h3 className={classes.subsectionTitle}>1.2 Information Collected Automatically</h3>
            <ul className={classes.list}>
              <li><strong>Device Information:</strong> Device type, operating system, unique device identifiers, browser type and version, and mobile network information</li>
              <li><strong>Log Data:</strong> IP address, access times, pages viewed, links clicked, and the page you visited before navigating to our Service</li>
              <li><strong>Usage Data:</strong> Features used, actions taken, session duration, and interaction patterns within the platform</li>
              <li><strong>Location Data:</strong> General geographic location inferred from your IP address</li>
              <li><strong>Cookies and Similar Technologies:</strong> We use cookies, web beacons, and similar tracking technologies to collect information about your browsing activities</li>
            </ul>

            <h3 className={classes.subsectionTitle}>1.3 Information from Third-Party Sources</h3>
            <ul className={classes.list}>
              <li><strong>Enterprise Platforms:</strong> When you connect enterprise applications (such as Workday, SAP, NetSuite, or Salesforce), we receive authentication tokens and limited user data necessary to facilitate secure access</li>
              <li><strong>Single Sign-On Providers:</strong> If you authenticate using SSO (e.g., Okta, Azure AD, Google Workspace), we receive basic profile information from your identity provider</li>
              <li><strong>Business Partners:</strong> We may receive information from resellers, integration partners, or referral sources</li>
            </ul>

            <h2 className={classes.sectionTitle}>2. How We Use Your Information</h2>
            <p className={classes.paragraph}>
              We use the information we collect for the following purposes:
            </p>

            <h3 className={classes.subsectionTitle}>2.1 Service Delivery</h3>
            <ul className={classes.list}>
              <li>Create and manage your account</li>
              <li>Provide, operate, and maintain the Service</li>
              <li>Authenticate users and authorize access to connected enterprise applications</li>
              <li>Process transactions and send related information, including purchase confirmations and invoices</li>
              <li>Provide customer support and respond to your requests</li>
            </ul>

            <h3 className={classes.subsectionTitle}>2.2 Service Improvement</h3>
            <ul className={classes.list}>
              <li>Analyze usage patterns to improve functionality and user experience</li>
              <li>Develop new features, products, and services</li>
              <li>Conduct research and analytics to understand how users interact with our Service</li>
              <li>Test and troubleshoot new features before release</li>
            </ul>

            <h3 className={classes.subsectionTitle}>2.3 Communication</h3>
            <ul className={classes.list}>
              <li>Send administrative messages, such as security alerts, system updates, and policy changes</li>
              <li>Respond to your comments, questions, and support requests</li>
              <li>Send promotional communications (you can opt out at any time)</li>
            </ul>

            <h3 className={classes.subsectionTitle}>2.4 Security and Compliance</h3>
            <ul className={classes.list}>
              <li>Detect, prevent, and address fraud, unauthorized access, and other illegal activities</li>
              <li>Monitor and analyze trends to detect security threats</li>
              <li>Enforce our Terms of Service and other agreements</li>
              <li>Comply with legal obligations, including responding to lawful requests from public authorities</li>
            </ul>

            <h2 className={classes.sectionTitle}>3. Cookies and Tracking Technologies</h2>
            <p className={classes.paragraph}>
              We use cookies and similar tracking technologies to collect and store information about
              your interactions with our Service.
            </p>

            <h3 className={classes.subsectionTitle}>Types of Cookies We Use</h3>
            <ul className={classes.list}>
              <li><strong>Essential Cookies:</strong> Required for the Service to function properly, including authentication and security</li>
              <li><strong>Analytics Cookies:</strong> Help us understand how users interact with our Service to improve functionality</li>
              <li><strong>Preference Cookies:</strong> Remember your settings and preferences for a better experience</li>
            </ul>
            <p className={classes.paragraph}>
              You can control cookies through your browser settings. Note that disabling certain cookies
              may affect the functionality of our Service.
            </p>

            <h2 className={classes.sectionTitle}>4. Disclosure of Your Information</h2>
            <p className={classes.paragraph}>
              We do not sell your personal information. We may share your information in the following circumstances:
            </p>
            <ul className={classes.list}>
              <li><strong>Service Providers:</strong> We share information with third-party vendors who perform services on our behalf, such as cloud hosting (AWS, Google Cloud), payment processing, analytics, and customer support. These providers are contractually obligated to protect your information.</li>
              <li><strong>Enterprise Platform Integrations:</strong> When you authorize connections to enterprise applications, we share necessary authentication and user data to enable access.</li>
              <li><strong>Legal Requirements:</strong> We may disclose information if required by law, subpoena, court order, or other legal process, or if we believe disclosure is necessary to protect our rights, your safety, or the safety of others.</li>
              <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, reorganization, or sale of assets, your information may be transferred as part of that transaction.</li>
              <li><strong>With Your Consent:</strong> We may share information for other purposes with your explicit consent.</li>
            </ul>

            <h2 className={classes.sectionTitle}>5. Data Security</h2>
            <p className={classes.paragraph}>
              We implement robust technical and organizational measures to protect your information:
            </p>
            <ul className={classes.list}>
              <li><strong>Encryption:</strong> All data is encrypted in transit (TLS 1.2+) and at rest (AES-256)</li>
              <li><strong>Access Controls:</strong> Role-based access controls and multi-factor authentication for internal systems</li>
              <li><strong>Infrastructure Security:</strong> Our infrastructure is hosted on SOC 2 Type II certified cloud providers</li>
              <li><strong>Regular Audits:</strong> We conduct regular security assessments and penetration testing</li>
              <li><strong>Incident Response:</strong> We maintain an incident response plan to address potential security breaches</li>
            </ul>
            <p className={classes.paragraph}>
              While we strive to protect your information, no method of transmission over the Internet
              or electronic storage is 100% secure. We cannot guarantee absolute security.
            </p>

            <h2 className={classes.sectionTitle}>6. Data Retention</h2>
            <p className={classes.paragraph}>
              We retain your information for as long as necessary to fulfill the purposes outlined in
              this Privacy Policy, unless a longer retention period is required or permitted by law.
              When determining retention periods, we consider:
            </p>
            <ul className={classes.list}>
              <li>The nature and sensitivity of the information</li>
              <li>The purposes for which we process your information</li>
              <li>Applicable legal, regulatory, or contractual requirements</li>
              <li>Whether the purpose of processing can be achieved by other means</li>
            </ul>
            <p className={classes.paragraph}>
              When your account is terminated, we will delete or anonymize your personal information
              within 90 days, except where retention is required for legal or legitimate business purposes.
            </p>

            <h2 className={classes.sectionTitle}>7. International Data Transfers</h2>
            <p className={classes.paragraph}>
              Databycloud is based in the United States. If you access our Service from outside the
              United States, your information may be transferred to, stored, and processed in the
              United States or other countries where our service providers operate. These countries
              may have data protection laws that differ from your country of residence.
            </p>
            <p className={classes.paragraph}>
              For transfers from the European Economic Area (EEA), United Kingdom, or Switzerland,
              we rely on appropriate safeguards such as Standard Contractual Clauses approved by
              the European Commission. By using our Service, you consent to the transfer of your
              information as described in this policy.
            </p>

            <h2 className={classes.sectionTitle}>8. Your Privacy Rights</h2>
            <p className={classes.paragraph}>
              Depending on your location, you may have certain rights regarding your personal information:
            </p>

            <h3 className={classes.subsectionTitle}>For All Users</h3>
            <ul className={classes.list}>
              <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
              <li><strong>Correction:</strong> Request that we correct inaccurate or incomplete information</li>
              <li><strong>Deletion:</strong> Request that we delete your personal information, subject to certain exceptions</li>
              <li><strong>Opt-Out:</strong> Unsubscribe from marketing communications at any time</li>
            </ul>

            <h3 className={classes.subsectionTitle}>Additional Rights for EEA/UK Residents (GDPR)</h3>
            <ul className={classes.list}>
              <li><strong>Restriction:</strong> Request that we restrict processing of your information</li>
              <li><strong>Portability:</strong> Receive your data in a structured, machine-readable format</li>
              <li><strong>Objection:</strong> Object to processing based on legitimate interests</li>
              <li><strong>Withdraw Consent:</strong> Withdraw consent where processing is based on consent</li>
              <li><strong>Lodge a Complaint:</strong> File a complaint with your local data protection authority</li>
            </ul>

            <h3 className={classes.subsectionTitle}>Additional Rights for California Residents (CCPA/CPRA)</h3>
            <ul className={classes.list}>
              <li><strong>Know:</strong> Request information about the categories and specific pieces of personal information we collect</li>
              <li><strong>Delete:</strong> Request deletion of your personal information</li>
              <li><strong>Non-Discrimination:</strong> We will not discriminate against you for exercising your privacy rights</li>
            </ul>

            <p className={classes.paragraph}>
              To exercise any of these rights, please contact us at{" "}
              <a href="mailto:privacy@databycloud.com" className={classes.link}>
                privacy@databycloud.com
              </a>
              . We will respond to your request within 30 days (or as required by applicable law).
              We may need to verify your identity before processing your request.
            </p>

            <h2 className={classes.sectionTitle}>9. Changes to This Privacy Policy</h2>
            <p className={classes.paragraph}>
              We may update this Privacy Policy from time to time to reflect changes in our practices,
              technologies, legal requirements, or other factors. When we make material changes, we
              will notify you by updating the "Last updated" date at the top of this policy and,
              where appropriate, provide additional notice (such as email notification or a prominent
              notice on our Service). We encourage you to review this Privacy Policy periodically to
              stay informed about how we protect your information.
            </p>

            <h2 className={classes.sectionTitle}>10. Contact Us</h2>
            <p className={classes.paragraph}>
              If you have questions, concerns, or requests regarding this Privacy Policy or our
              data practices, please contact us:
            </p>
            <p className={classes.paragraph}>
              <strong>Databycloud, Inc.</strong><br />
              Email:{" "}
              <a href="mailto:privacy@databycloud.com" className={classes.link}>
                privacy@databycloud.com
              </a><br />
              Website:{" "}
              <a href="https://databycloud.com" className={classes.link}>
                https://databycloud.com
              </a>
            </p>
            <p className={classes.paragraph}>
              For data protection inquiries from the European Union, you may also contact our
              Data Protection Officer at{" "}
              <a href="mailto:dpo@databycloud.com" className={classes.link}>
                dpo@databycloud.com
              </a>.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
